import React, { useCallback, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { formatDateSafe, formatCurrency } from "utils/helpers/accounting";
import { ROUTES } from "utils";
import { useNavigate } from "react-router-dom";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
const todayString = formatDate(today);

type ARDetailQuery = {
  from_date: string;
  to_date: string;
  partner_id?: string;
  report_type: "RECEIVABLE" | "PAYABLE";
};

const RpAccountReceivableDetail: React.FC = () => {
  const navigate = useNavigate();
  const { fetchRpAccountReceivableDetail } = useReport();
  const [filters, setFilters] = useState<ARDetailQuery>({
    from_date: firstOfMonth,
    to_date: todayString,
    report_type: "RECEIVABLE",
  });
  const [submitted, setSubmitted] = useState<ARDetailQuery | null>(null);

  const handleFilterChange = useCallback((key: keyof ARDetailQuery, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) return;
      setSubmitted(filters);
      fetchRpAccountReceivableDetail.mutate({
        from_date: filters.from_date,
        to_date: filters.to_date,
        type: filters.report_type,
      });
    },
    [fetchRpAccountReceivableDetail, filters],
  );

  const records = (fetchRpAccountReceivableDetail.data?.data ?? []) as any[];

  const columns = useMemo(
    () => [
      {
        headerName: "STT",
        valueGetter: (p: any) => (p.node?.rowIndex ?? 0) + 1,
        pinned: "left",
        minWidth: 60,
        maxWidth: 70,
        cellClass: "text-center text-gray-500 text-sm",
      },
      {
        headerName: "Chứng từ",
        children: [
          {
            headerName: "Số chứng từ",
            field: "voucher_code",
            minWidth: 140,
            cellClass: "text-blue-600 font-medium",
          },
          {
            headerName: "Ngày chứng từ",
            field: "accounting_date",
            minWidth: 130,
            valueFormatter: (p: any) => formatDateSafe(p.value),
          },
        ],
      },
      {
        headerName: "Diễn giải",
        field: "description",
        minWidth: 250,
        flex: 1,
      },
      {
        headerName: "Số tiền",
        children: [
          {
            headerName: "Nợ",
            field: "debit_amount",
            minWidth: 130,
            valueFormatter: (p: any) => p.value != null && p.value !== 0 ? formatCurrency(Number(p.value)) : "",
            cellClass: "text-right text-red-600 font-medium",
          },
          {
            headerName: "Có",
            field: "credit_amount",
            minWidth: 130,
            valueFormatter: (p: any) => p.value != null && p.value !== 0 ? formatCurrency(Number(p.value)) : "",
            cellClass: "text-right text-green-700 font-medium",
          },
        ],
      },
      {
        headerName: "Ghi chú",
        field: "note",
        minWidth: 160,
        cellClass: "text-gray-500 text-sm italic",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: "agTextColumnFilter", floatingFilter: true, minWidth: 100 }),
    [],
  );

  const isLoading = fetchRpAccountReceivableDetail.isPending;

  return (
    <div className="p-6 space-y-5">
      {isLoading && <LoadingState message="Đang tải báo cáo" description="Vui lòng chờ trong giây lát." />}

      <nav className="text-sm text-blue-600 flex items-center gap-1 flex-wrap">
        <span className="cursor-pointer hover:underline font-medium" onClick={() => navigate(ROUTES.PRIVATE_ROOT.DASHBOARD)}>BÁO CÁO</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-500 cursor-pointer hover:underline">Sổ sách, báo cáo quản trị</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-700 font-medium">Sổ chi tiết công nợ</span>
      </nav>

      <h1 className="text-xl font-bold text-gray-900">Sổ chi tiết công nợ</h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Từ ngày <span className="text-red-500">*</span></label>
            <input type="date" value={filters.from_date} onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" required />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Đến ngày <span className="text-red-500">*</span></label>
            <input type="date" value={filters.to_date} onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" required />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Khách hàng, NCC liên quan</label>
            <input type="text" value={filters.partner_id || ""} onChange={(e) => handleFilterChange("partner_id", e.target.value || undefined)}
              placeholder="Lựa chọn..."
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Loại báo cáo</label>
            <div className="relative">
              <select
                value={filters.report_type}
                onChange={(e) => handleFilterChange("report_type", e.target.value as "RECEIVABLE" | "PAYABLE")}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none appearance-none pr-8"
              >
                <option value="RECEIVABLE">Khoản phải thu</option>
                <option value="PAYABLE">Khoản phải trả</option>
              </select>
              <button type="button" onClick={() => handleFilterChange("report_type", "RECEIVABLE")}
                className="absolute right-7 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                ×
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-start gap-2">
          <button type="submit" disabled={isLoading}
            className="rounded border border-blue-600 px-4 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition">
            Xem số liệu
          </button>
          <button type="button" onClick={handleSubmit} disabled={isLoading}
            className="rounded border border-blue-600 px-4 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition">
            Xem báo cáo
          </button>
          <button type="button"
            className="rounded border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
            Xuất file Excel
          </button>
        </div>
      </form>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {submitted ? (
          <>
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">
                {records.length} bản ghi · {submitted.from_date} → {submitted.to_date}
              </h2>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${filters.report_type === "RECEIVABLE" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                {filters.report_type === "RECEIVABLE" ? "Khoản phải thu" : "Khoản phải trả"}
              </span>
            </div>
            <AgGridTable rows={records} columns={columns as any} defaultColDef={defaultColDef} pagination={true} autoHeight />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <svg className="w-12 h-12 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-sm">Nhấn <strong>Xem số liệu</strong> hoặc <strong>Xem báo cáo</strong> để tải dữ liệu</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RpAccountReceivableDetail;
